import React, {useEffect, useState} from "react"
import {Col, Container, Row} from "react-bootstrap"
import '../chart.css'
import {CardSection} from "../common/Card"
import {BaselineInterval, DataClient, DateRange, PowerEntry} from "../../client/DataClient"
import {EventScheduleClient, EventWithDetails, TestScheduleError} from "../../client/EventScheduleClient"
import {BuildingClient, ControlStrategy} from "../../client/BuildingClient"
import {Building} from "../BuildingStatus"
import {DateTime} from "../../time/DateTime"
import {DatePicker} from "@mui/x-date-pickers"
import {Select, SelectOption} from "../common/MuiSelect"
import {meteringOptionalRoute} from "../../Routes"
import {useNavigate, useParams, useSearchParams} from "react-router-dom"
import {BuildingProperties} from "./BuildingProperties"
import {NoDataAvailable, ReadingsChart} from "./ReadingsChart"
import {EventTable} from "./EventTable"
import {AsyncDeviceStatusHeader} from "./AsyncDeviceStatusHeader";
import {MeterClient} from "../../client/MeterClient";

export interface ConsumptionPageProps {
    buildingClient: BuildingClient
    meterClient: MeterClient
    dataClient: DataClient
    eventClient: EventScheduleClient
    disableVirtualizationForTesting?: boolean
}

interface ReadingsAndEventsProps {
    readings: PowerEntry[]
    events: EventWithDetails[]
    brushRange: [number, number] | undefined
    setBrushRange: React.Dispatch<
        React.SetStateAction<[number, number] | undefined>
    >
    chartRange: () => DateRange | undefined
    selectedBuilding: Building
    scheduleEvent: (buildingId: number, controlStrategyId?: number) => Promise<TestScheduleError | null>
    buildingClient: BuildingClient
    controlStrategies: ControlStrategy[]
}


const ReadingsAndEvents: React.FC<ReadingsAndEventsProps> = ({
                                                                 readings,
                                                                 events,
                                                                 brushRange,
                                                                 setBrushRange,
                                                                 chartRange,
                                                                 selectedBuilding,
                                                                 scheduleEvent,
                                                                 buildingClient,
                                                                 controlStrategies,
                                                             }) => {
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0)


    return (
        <CardSection
            header={
                <AsyncDeviceStatusHeader scheduleEvent={scheduleEvent} receiveMonitorData={(buildingId) => buildingClient.retrieveMonitorData(buildingId)} buildingId={selectedBuilding.id} controlStrategies={controlStrategies}/>
            }
        >
            {readings.length === 0 ? (
                <NoDataAvailable />
            ) : (
                <ReadingsChart
                    key={refreshTrigger}
                    readings={readings}
                    events={events}
                    brushRange={brushRange}
                    setBrushRange={setBrushRange}
                    chartRange={chartRange}
                />
            )}

            {/* Events Table Section */}
            {selectedBuilding && events.length > 0 && (
                <div className="mt-4">
                    <EventTable
                        readings={readings}
                        events={events}
                        setBrushRange={setBrushRange}
                        setRefreshTrigger={setRefreshTrigger}
                        refreshTrigger={refreshTrigger}
                        chartRange={chartRange}
                    />
                </div>
            )}
        </CardSection>
    )
}


function setBrushLength(now: string, date: string, transformedReadings: PowerEntry[], setBrushRange: (value: (((prevState: ([number, number] | undefined)) => ([number, number] | undefined)) | [number, number] | undefined)) => void) {
    const isToday = () => now == date

    if (isToday() && transformedReadings.length > 12 * 2) {
        setBrushRange([transformedReadings.length - 12 * 2, transformedReadings.length - 1])
    } else {
        setBrushRange([0, transformedReadings.length - 1])
    }
}

export function includeBaseline(powers: PowerEntry[], baselineIntervals: BaselineInterval[], expectedDropInKilowatts: number): (PowerEntry & {baseline?: number, expected?: number })[]  {
    const includes =  (p: PowerEntry) => (i: BaselineInterval) =>
        new DateRange(DateTime.fromISO(i.from), DateTime.fromISO(i.to)).contains(DateTime.fromSeconds(p.date))

    return powers.map ( p => {
        const found = baselineIntervals.find(includes(p))
        const baseline = found ? found.baselineInKilowatts / 1000.0 : undefined
        const expected = found ? (found.baselineInKilowatts - expectedDropInKilowatts) / 1000.0 : baseline
        return {...p, baseline: baseline, expected: expected}
    })
}

export const ConsumptionPage = ({ buildingClient, meterClient, dataClient, eventClient, disableVirtualizationForTesting }: ConsumptionPageProps) => {

    const { buildingId }  = useParams()
    const [searchParams, setSearchParams] = useSearchParams();

    const now: string = DateTime.local().toISODate()!

    const date = searchParams.get('date') || DateTime.local().toISODate();

    useEffect(() => {
        if (!searchParams.get('date')) {
            setSearchParams({ date: DateTime.local().toISODate() }, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const [buildings, setBuildings] = useState<Building[]>([])
    const [events, setEvents] = useState<EventWithDetails[]>([])
    const [readings, setReadings] = useState<PowerEntry[]>()
    const [brushRange, setBrushRange] = useState<[number, number]>()
    const [controlStrategies, setControlStrategies] = useState<ControlStrategy[]>([])

    const navigate = useNavigate()

    useEffect(() => {
        buildingClient
            .retrieveBuildings()
            .then(setBuildings)
            .catch((err) => console.error(err))
    }, [buildingClient])

    const selectedBuilding = (buildings.length > 0) ?
            buildingId
                ? buildings.find(b => b.id === Number(buildingId))
                : buildings[0]
        : undefined

    useEffect(() => {
        if (!selectedBuilding) return

        const fetchData = async (building: Building) => {
            const controlStrategiesForBuilding = (await buildingClient.retrieveBuildingStrategies(selectedBuilding.id)).unwrap();
            setControlStrategies(controlStrategiesForBuilding)
            const oneDayRange = new DateRange(DateTime.fromISO(date), DateTime.fromISO(date).endOf('day'))
            const transformedReadings = await dataClient.powers(building.id, oneDayRange)
            const baselineResult = await dataClient.retrieveBaseline(building.id, oneDayRange, "PreviousHhAverage")
            const powerWithBaseline = includeBaseline(transformedReadings, baselineResult.unwrap(), controlStrategiesForBuilding[0].saving)
            const events = (await eventClient.getEvents(oneDayRange.start, oneDayRange.end.plus({days: 1}), [building.id]))
                .filter(notUnavailability)
                .filter(isEventDetails)

            setReadings(powerWithBaseline)
            setBrushLength(now, date, powerWithBaseline, setBrushRange)

            setEvents(
                events.filter(e =>
                    !e.eventStatus.cancelledNeverSentToDevice() &&
                    !e.eventStatus.cancelledAndSuccessfullyRemovedFromDevice()
                )
            )
        }

        fetchData(selectedBuilding).catch(err => console.error(err))

    }, [selectedBuilding, date])


    const isEventDetails = (event: EventWithDetails) : event is EventWithDetails  => event.eventStatus !== undefined
    const notUnavailability = (event: EventWithDetails) : event is EventWithDetails => event.eventType.type !== 'unavailability'

    const chartRange = () =>
        readings && readings.length > 0 ? new DateRange(DateTime.fromSeconds(readings[0].date).toLondonTimeZone(), DateTime.fromSeconds(readings[readings.length - 1].date).toLondonTimeZone()) : undefined

    const dateChange = (newDate: DateTime | null) => {
        if (!newDate) return;

        const dateOnly = newDate.toISODate();
        if (dateOnly) {
            // Create a new URLSearchParams object from the existing one
            const newSearchParams = new URLSearchParams(searchParams);

            // Set the new value on the new object
            newSearchParams.set('date', dateOnly);

            // Pass the new object to the setter function
            setSearchParams(newSearchParams);
        }
    };

    const selectBuilding = (e: SelectOption) => {
        const building = buildings.find(b => `${b.id}` === e.id)
        if (building) {
            navigate(meteringOptionalRoute.withParams(building.id))
        }
    }

    return (
        <Container fluid className="pt-3">
            {ControlToolbar(selectedBuilding, selectBuilding, buildings, date, dateChange)}
            {/* Consumption Chart Section */}
            {selectedBuilding && readings && <ReadingsAndEvents readings={readings} events={events} brushRange={brushRange} setBrushRange={setBrushRange } chartRange={chartRange }
                                                                selectedBuilding={selectedBuilding}
                                                                buildingClient={buildingClient}
                                                                scheduleEvent={(_, controlStrategyId) => eventClient.scheduleTestEvent(selectedBuilding.id, controlStrategyId)}
                                                                controlStrategies={controlStrategies}
            />}
            {/* Building Info Section */}
            {selectedBuilding && (
                <CardSection header="Site Information" className="mt-4">
                    <BuildingProperties
                        buildingClient={buildingClient}
                        meterClient={meterClient}
                        selectedBuilding={selectedBuilding}
                        navigate={navigate}
                        disableVirtualizationForTesting={disableVirtualizationForTesting}
                    />
                </CardSection>
            )}
        </Container>
    )
}


const ControlToolbar = (selectedBuilding: Building | undefined, selectBuilding: (e: SelectOption) => void, buildings: Building[], date: string, dateChange: (e) => void)=>
    <Row className="mb-4 align-items-center">
        <Col xs={12} md={6}>
            <Select
                label="Site"
                data-testid="select-building"
                initialValue={
                    selectedBuilding
                        ? {id: `${selectedBuilding.id}`, label: selectedBuilding.name}
                        : undefined
                }
                onChange={selectBuilding}
                options={buildings.map(b => ({id: `${b.id}`, label: b.name}))}
            />
        </Col>
        <Col xs={12} md={6}>
            <DatePicker
                value={DateTime.fromISO(date).raw()}
                label="Date"
                onChange={dateChange}
                disableFuture
            />
        </Col>
    </Row>
